import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-login-page.scss';

const AdminLoginPage: React.FC = () => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { adminLogin, user, isLoggedinAdmin } = useContext(AuthContext);
    const navigate = useNavigate();
    
    // Set page title
    useEffect(() => {
        document.title = 'Admin Login | Northern Championships Series';
    }, []);
    
    // Redirect to dashboard if already logged in as admin
    useEffect(() => {
        if (isLoggedinAdmin) {
            navigate('/admin/dashboard');
        }
    }, [isLoggedinAdmin, navigate]);
    
    // If not logged in at all, prompt to login first
    if (!user) {
        return (
            <div className="admin-login-container">
                <div className="admin-login-card">
                    <h1>Admin Login</h1>
                    <p className="description">
                        You need to log in with Steam first before accessing the admin panel.
                    </p>
                    <div className="back-link">
                        <Link to="/">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            // Validate input
            if (!password.trim()) {
                setError('Password is required');
                setLoading(false);
                return;
            }
            
            // Attempt to login
            const success = await adminLogin(password);
            
            if (success) {
                // Navigate to admin dashboard on success
                navigate('/admin/dashboard');
            } else {
                setError('Invalid password. Please try again.');
            }
        } catch (err) {
            setError('An error occurred during login. Please try again.');
            console.error('Admin login error:', err);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="admin-login-container">
            <div className="admin-login-card">
                <h1>Admin Login</h1>
                <p className="description">
                    Enter your admin password to access the admin panel.
                </p>
                
                <div className="user-info">
                    Logging in as: <strong>{user.playerName}</strong> ({user.steamID})
                </div>
                
                {error && <div className="error-message">{error}</div>}
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">Admin Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your admin password"
                            disabled={loading}
                        />
                    </div>
                    
                    <button 
                        type="submit" 
                        className="admin-login-button"
                        disabled={loading}
                    >
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                
                <div className="back-link">
                    <Link to="/">
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;